import { motion } from 'framer-motion';
import { 
  ChatCircle, 
  FileText, 
  Gear, 
  HardHat, 
  Key, 
  Headset 
} from '@phosphor-icons/react';

const processSteps = [
  {
    number: '01',
    title: 'Kostenlose Erstberatung',
    description: 'In der kostenlosen Erstberatung ermitteln wir gemeinsam Ihre Anforderungen und Projektziele.',
    icon: ChatCircle
  },
  {
    number: '02',
    title: 'Individuelles Angebot',
    description: 'Nach der Erstberatung erstellen wir ein maßgeschneidertes Angebot mit detaillierten Angaben.',
    icon: FileText
  },
  {
    number: '03',
    title: 'Planung und Abstimmung',
    description: 'Nun geht es an die konkrete Planung. Wir stimmen die Projektziele und Meilensteine ab.',
    icon: Gear
  },
  {
    number: '04',
    title: 'Umsetzung und Qualitätskontrolle',
    description: 'Unsere Umsetzung erfolgt präzise und nach höchsten Qualitätsstandards mit laufenden Kontrollen.',
    icon: HardHat
  },
  {
    number: '05',
    title: 'Übergabe und Abschlussprüfung',
    description: 'Zum Abschluss des Projekts übergeben wir Ihnen das Endergebnis mit gründlicher Prüfung.',
    icon: Key
  },
  {
    number: '06',
    title: 'Nachbetreuung und Support',
    description: 'Auch nach Projektabschluss bleiben wir Ihr Ansprechpartner für Fragen und Anpassungen.',
    icon: Headset
  }
];

const ProcessSection = () => {
  const scrollToForm = () => {
    const formSection = document.getElementById('lead-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-24 md:py-32 bg-dark" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-gold font-sans text-sm uppercase tracking-[0.2em] mb-4 block">
            Unser Prozess
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Ihr Weg zum neuen Zuhause
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-base sm:text-lg">
            Von der ersten Beratung bis zur schlüsselfertigen Übergabe — 
            wir begleiten Sie durch jeden Schritt.
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {processSteps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="glass-card p-6 md:p-8 h-full transition-all duration-300">
                {/* Step Number */}
                <div className="flex items-center justify-between mb-6">
                  <span className="font-serif text-3xl text-gold/30 group-hover:text-gold/50 transition-colors">
                    {step.number}
                  </span>
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <step.icon size={24} className="text-gold" weight="light" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-serif text-xl text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Connector Line (hidden on last item in row) */}
              {(index + 1) % 3 !== 0 && index < processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-white/10" />
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <button
            onClick={scrollToForm}
            className="bg-gold text-black font-medium uppercase tracking-wide text-sm px-8 py-4 hover:bg-gold-light transition-all inline-flex items-center gap-2"
            data-testid="process-cta"
          >
            Jetzt starten
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;
